import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {connect, useSelector} from "react-redux";
import {matchPath, useLocation} from "react-router";
import {css, Theme} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {CenterBox, Hidden, WideContent} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, mb, mt, mv, p, pl, pt} from "@pg-design/helpers-css";
import {BrandBuildingIcon, BrandMapPinIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useChangeVisibility, useIsMounted} from "@pg-mono/hooks";
import {rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {rpCommonUrl} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {PhoneModal} from "../../application/components/phone_modal/PhoneModal";
import {ApplicationSource} from "../../application/utils/ApplicationSource";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {Heading} from "../../atoms/Heading";
import {LocationLinks} from "../../atoms/LocationLinks";
import {useOfferBuildings} from "../../hooks/useOfferBuildings";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {FavouritesInfoModal} from "../../my_offers/components/FavouritesInfoModal/FavouritesInfoModal";
import {backToUrl, IBackToUrlAction} from "../../navigation/actions/back_to_url_actions";
import {SubscribeButton} from "../../real_estate/components/SubscribeButton";
import {Country} from "../../region/types/Country";
import {offerDetailPageview} from "../../tracking/algolytics_hits/offer_detail_pageview";
import {IGtmRegion} from "../../tracking/google_tag_manager/calculate_gtm_dimensions";
import {gtmOfferGalleryEvents} from "../../tracking/google_tag_manager/gtm_event_offer_gallery_modal";
import {gtmInvestDetailOnOfferDetail} from "../../tracking/google_tag_manager/gtm_invest_detail";
import {IVariables} from "../../variables/reducers/variables_reducer";
import {ViewType} from "../../view_type/ViewType";
import {OfferAndPropertyBreadcrumbs} from "../atoms/OfferAndPropertyBreadcrumbs";
import {IRecommendedRegion} from "../atoms/OfferLocationLinksRecommendedRegions";
import {OfferLocationLinksWrapper} from "../atoms/OfferLocationLinksWrapper";
import {DetailConstructionGalleryCTA} from "../components/DetailConstructionGalleryCTA";
import {GalleryHolder} from "../components/gallery_holder/GalleryHolder";
import {NearbyOffersMap} from "../components/NearbyOffersMap";
import {OfferDetailHeader} from "../components/offer_detail_header/OfferDetailHeader";
import {IOfferDetailPromotion} from "../detail/actions/fetch_offer_detail_promotions";
import {OfferDetailForSale} from "../detail/components/OfferDetailForSale";
import {offerDescriptionSectionHeader, OfferDetailLimited} from "../detail/components/OfferDetailLimited";
import {OfferDetailStages} from "../detail/components/stages/OfferDetailStages";
import {Planner3dModal} from "../detail/planner3D/components/Planner3D";
import {SchemaOfferDetail} from "../detail/schema/SchemaOfferDetail";
import {SchemaOfferDetailRating} from "../detail/schema/SchemaOfferDetailRating";
import {buildFriendlyOfferListLink} from "../helpers/friendly_offer_list/build_friendly_offer_list_link";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {IOfferArticle} from "../list/actions/fetch_offer_list_articles";
import {IRelatedOfferList} from "../list/types/IOfferListResponse";
import {IOfferDetail} from "../types/IOfferDetail";
import {OfferModalActiveTab} from "../types/OfferModalActiveTab";
import {OfferType} from "../types/OfferType";
import {getOfferDisplayStatus} from "../utils/get_offer_display_status";
import {isOfferArchived} from "../utils/is_offer_archived";
import {isOfferFavouriteButtonAvailable} from "../utils/is_offer_favourite_button_available";
import {offerPlural} from "../utils/offer_plural";

const OfferChatBot = React.lazy(() => import("../detail/components/OfferChatBot"));

interface IStateProps {
    offer: IOfferDetail | null;
    articles: IOfferArticle[];
    recommendedRegions: IRecommendedRegion[];
    relatedOffers: IRelatedOfferList[];
    promotions: IOfferDetailPromotion[];
    variables: IVariables | null;
}

interface IActionsProps {
    backToUrl: (url: string, text: string) => IBackToUrlAction;
}

interface IOwnProps {}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

const OfferDetailViewC = (props: IProps) => {
    const {offer, promotions, variables, relatedOffers, recommendedRegions} = props;
    const viewType = useSelector((store: IRPStore) => store.viewType.current);
    const isGTMInitialized = useSelector((store: IRPStore) => store.googleTagManager);
    const {isMobile} = useUserDevice();

    const isMounted = useIsMounted();

    const getFriendlyOfferListLink = () => {
        const isFlat = offer?.type === OfferType.FLAT;
        const stats = offer?.region?.stats;

        const isEmpty = isFlat ? (stats?.properties_flats_count_for_sale ?? 0) <= 0 : (stats?.properties_houses_count_for_sale ?? 0) <= 0;

        const regionSlug = isEmpty ? offer?.region?.parent?.slug : offer?.region?.slug;
        const country = offer?.region?.country;
        const type = isFlat ? OfferType.FLAT : OfferType.HOUSE;

        return buildFriendlyOfferListLink({type, region: regionSlug, country});
    };

    const regionName =
        offer !== null &&
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.name
                : offer.region.name
            : offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.name
              : offer.region.name);

    useEffect(() => {
        if (offer) {
            offerDetailPageview(offer, offer.vendor);
        }
        const prevUrl = document.referrer;
        if (prevUrl !== "") {
            const url = new URL(prevUrl);
            const baseUrl = `${url.protocol}//${url.host}`;
            if (baseUrl === rpCommonUrl && backToPaths.some((path: string) => matchPath(url.pathname, {path: path, exact: true, strict: true}) !== null)) {
                props.backToUrl(prevUrl, "OFERTY");
                return;
            }
        }
        props.backToUrl(getFriendlyOfferListLink(), `oferty ${regionName}`);
        // props.gtmProductDetailOnOfferDetail();
        // props.gtmProductClickOnOfferDetail();
    }, [offer]);

    /*
    This particular event has to be fired only once. Event checks if gtm is initialized and offer data is available.
    Because of SSR offer is already available and dependency in useEffect is set to GTM flag from store.
    This is crucial for the event to fire in correct order.
     */
    useEffect(() => {
        if (offer && isGTMInitialized) {
            gtmInvestDetailOnOfferDetail(offer);
        }
    }, [isGTMInitialized]);

    const [isModalAndTabActive, _setIsModalAndTabActive] = useState<OfferModalActiveTab | null>(null);
    const setIsModalAndTabActive = (tab: OfferModalActiveTab | null) => {
        if (tab && isModalAndTabActive === null) {
            // send this GTM event only when opening modal
            gtmOfferGalleryEvents.onOfferGalleryModalOpen(tab, viewType);
        }
        _setIsModalAndTabActive(tab);
    };

    const backToPaths = [rpAppPath.friendlyOfferList, rpAppPath.multiOfferList.base, rpAppPath.offer.list];
    const isForSale = offer?.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isPlanned = offer?.configuration.display_type === OfferDisplayType.PLANNED;
    const isSold = offer?.configuration.display_type === OfferDisplayType.SOLD;
    const isLimited = offer && getOfferDisplayStatus(offer) === "limited";
    const isArchived = offer && isOfferArchived(offer);
    const isClassicView = isForSale && !isLimited;
    const enableChatBot = isMounted && isForSale && !isLimited; // disable ChatBot if isForSaleOverLimit
    const regionStatsForSale =
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.stats && offer.region.parent.stats.properties_flats_count_for_sale
                : offer.region.stats?.properties_flats_count_for_sale
            : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.stats && offer.region.parent.stats.properties_houses_count_for_sale
              : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale) || false;

    const refGallery = useRef<HTMLTableSectionElement>(null);
    const refProperties = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refDescription = useRef<HTMLTableSectionElement>(null);
    const refLocation = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(
        () => ({
            refGallery,
            refProperties,
            refDescription,
            refPromotions,
            refLocation,
            refContact
        }),
        []
    );

    const hasProperties = offer != null ? (offer.stats?.properties_count_for_sale ?? 0) > 0 : false;
    const hasPromotions = promotions.length > 0;
    const hasDescription = offer != null ? !!(offer.description || offer.configuration.show_generated_description) : false;

    const {offerBuildings, fetchOfferBuildings} = useOfferBuildings();

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const currentStageOfferCount = offer?.groups?.stages.filter((stage) => stage.current);
    const location = useLocation();
    useEffect(() => {
        if (!!isModalAndTabActive) {
            setModalIsOpen(true);
        }
    }, [isModalAndTabActive]);

    useEffect(() => {
        if (!modalIsOpen) {
            setIsModalAndTabActive(null);
        }
    }, [modalIsOpen]);

    const changeModalTab = useCallback(
        (tab: OfferModalActiveTab): void => {
            setIsModalAndTabActive(tab);

            offer?.configuration.show_building_models_on_map && fetchOfferBuildings(offer?.id);
        },
        [offer, fetchOfferBuildings]
    );

    // Scroll and visibility solution
    const getElems = useCallback(() => {
        const res = [];
        if (hasProperties) {
            res.push("properties");
        }
        if (hasPromotions) {
            res.push("promotions");
        }
        if (hasDescription) {
            res.push("description");
        }
        res.push("location");
        res.push("contact");
        return res;
    }, [hasProperties, hasPromotions, hasDescription]);

    const [visibleElem, changeVisibility, viewportHeight] = useChangeVisibility(getElems, 100);

    /**
     * Render
     */

    if (offer == null) {
        return (
            <CenterBox height="500px">
                <Loader size="lg" />
            </CenterBox>
        );
    }
    const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
        offer.region.stats || {};

    const breadcrumbs = {
        region_type_voivodeship: region_type_voivodeship || null,
        region_type_city: region_type_city || null,
        region_type_county: region_type_county || null,
        region_type_district: region_type_district || null,
        region_type_housing_estate: region_type_housing_estate || null,
        region_type_town: region_type_town || null
    };
    const {vendor} = offer;

    const renderOfferDetail = () => {
        if (isClassicView) {
            return (
                <OfferDetailForSale
                    offer={offer}
                    promotions={promotions}
                    variables={variables}
                    refElems={refElems}
                    visibleElement={visibleElem}
                    viewportHeight={viewportHeight}
                    changeVisibility={changeVisibility}
                    changeModalTab={changeModalTab}
                    hasProperties={hasProperties}
                    hasDescription={hasDescription}
                    shouldMapBeRendered={!modalIsOpen}
                />
            );
        } else {
            return (
                <OfferDetailLimited
                    offer={offer}
                    refElems={refElems}
                    variables={variables}
                    viewportHeight={viewportHeight}
                    changeVisibility={changeVisibility}
                    visibleElement={visibleElem}
                    changeModalTab={changeModalTab}
                    hasDescription={hasDescription}
                    shouldMapBeRendered={!modalIsOpen}
                />
            );
        }
    };

    return (
        <>
            <article>
                <section>
                    <WideContent css={mt(2)}>
                        <OfferAndPropertyBreadcrumbs
                            country={offer.region.country.toString()}
                            type={offer.type}
                            breadcrumbs={breadcrumbs}
                            offer={{name: offer.name, slug: offer.slug, id: offer.id}}
                            groups={offer.groups}
                            vendor={{name: vendor.name, id: vendor.id, slug: vendor.slug}}
                        />
                    </WideContent>
                </section>

                <WideContent css={headerWrapper}>
                    <OfferDetailHeader offer={offer} onLocationClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP)} />
                </WideContent>

                {isPlanned && !isArchived && (
                    <WideContent>
                        <div css={mainMessageWrap}>
                            <Text css={mainMessageTitleStyle}>Inwestycja planowana</Text>
                            <Text variant="body_copy_2">
                                Uwaga! Deweloper nie rozpoczął jeszcze sprzedaży mieszkań w tej inwestycji. {regionStatsForSale && `Mamy dla Ciebie`}{" "}
                                {regionStatsForSale && (
                                    <a css={noWrapUnderlinedStyle} href={getFriendlyOfferListLink()}>
                                        {regionStatsForSale} {offerPlural(regionStatsForSale)}
                                    </a>
                                )}{" "}
                                {regionStatsForSale && `w\xa0najbliższej okolicy!`}
                            </Text>
                            {isSold && (
                                <>
                                    <InactiveInfo />
                                    <NearestOffersInfo regionStatsForSale={regionStatsForSale} friendlyOfferListLink={getFriendlyOfferListLink()} />
                                </>
                            )}
                        </div>
                    </WideContent>
                )}

                {isArchived && !isPlanned && (
                    <>
                        <WideContent>
                            <div css={mainMessageWrap}>
                                <Text css={mainMessageTitleStyle}>Oferta archiwalna</Text>

                                {(isSold || isLimited) && (
                                    <>
                                        <InactiveInfo />
                                        <NearestOffersInfo
                                            regionStatsForSale={regionStatsForSale}
                                            friendlyOfferListLink={currentStageOfferCount ? location.pathname : getFriendlyOfferListLink()}
                                            currentStageOfferCount={
                                                currentStageOfferCount ? currentStageOfferCount[0].offer.stats.properties_count_for_sale : null
                                            }
                                        />
                                    </>
                                )}
                            </div>
                        </WideContent>
                        <OfferDetailStages offer={offer} isArchived />
                    </>
                )}

                {!isClassicView && (
                    <WideContent>
                        {relatedOffers.length > 0 && (
                            <>
                                <Heading type="h2" css={offerDescriptionSectionHeader}>
                                    Najbliższe inwestycje
                                </Heading>

                                <div css={mapWrapper}>
                                    <NearbyOffersMap
                                        viewType={ViewType.OFFER}
                                        originCountry={offer.region.country}
                                        originCoordinates={offer.geo_point.coordinates}
                                        offers={relatedOffers}
                                    />
                                </div>
                            </>
                        )}

                        {(isSold || (isLimited && !isPlanned)) && (
                            <Heading type="h2" css={offerDescriptionSectionHeader}>
                                Prezentacja oferty archiwalnej
                            </Heading>
                        )}
                    </WideContent>
                )}

                <section id="galeria" ref={refElems.refGallery}>
                    <GalleryHolder
                        source={ApplicationSource.Offer}
                        offer={offer}
                        offerBuildings={offerBuildings}
                        isOpen={modalIsOpen}
                        handleGalleryModalChange={(tab) => setIsModalAndTabActive(tab)}
                        modalState={isModalAndTabActive}
                        onModalClose={() => {
                            setModalIsOpen(false);
                        }}
                    />
                </section>
                <Hidden on="desktop">
                    <WideContent>
                        {isOfferFavouriteButtonAvailable(offer) && <SubscribeButton css={subscribeButtonStyle} offerId={offer.id} size="m" />}
                        <Planner3dModal offer={offer} vendorId={offer.vendor.id} css={planner3DStyle} routedModalId="view">
                            <Button iconLeft={BrandBuildingIcon} variant="brand_icon" size={isMobile ? "medium" : "big"} css={[mb(2), pl(0)]}>
                                Zobacz budynek 3D
                            </Button>
                        </Planner3dModal>
                        <Button
                            size="medium"
                            variant="brand_icon"
                            iconLeft={BrandMapPinIcon}
                            onClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP)}
                            css={[mb(2), pl(0)]}
                        >
                            Zobacz na mapie
                        </Button>
                        <DetailConstructionGalleryCTA onButtonClick={() => setIsModalAndTabActive(OfferModalActiveTab.CONSTRUCTION_GALLERY)} />
                    </WideContent>
                </Hidden>

                {renderOfferDetail()}

                {props.articles.length > 0 && <RelatedArticlesSection heading="Artykuły powiązane:" articles={props.articles} />}

                <SchemaOfferDetail offer={offer} />
                {!isSold && offer.is_active && <SchemaOfferDetailRating offer={offer} />}

                {/* For now, we don't want to show abroad regions on offers listing view - so we don't want to show links to listings */}
                {props.offer && props.offer.region.country === Country.POLAND && (
                    <OfferLocationLinksWrapper css={linksWrapperStyle}>
                        <LocationLinks recommendedRegions={recommendedRegions} offerType={offer.type} region={offer.region} relatedOffers={relatedOffers} />
                    </OfferLocationLinksWrapper>
                )}

                {/*    <LeadGeneratorSuccessInfo />*/}
            </article>
            <FavouritesInfoModal />
            <LeadModal />
            {enableChatBot && (
                <React.Suspense fallback={<React.Fragment />}>
                    <OfferChatBot hasPromotions={!!promotions.length} offer={offer} />
                </React.Suspense>
            )}

            {!isArchived && (
                <PhoneModal
                    source={ApplicationSource.OfferPhoneCall}
                    vendorLogo={offer.vendor.logo?.v_log_80x60}
                    vendorId={offer.vendor.id}
                    offerId={props.offer?.id}
                    region={props.offer?.region as IGtmRegion}
                />
            )}
        </>
    );
};

export const OfferDetailView = connect<IStateProps, IActionsProps, IOwnProps, IRPStore>(mapStateToProps, mapActionsToProps)(OfferDetailViewC);

/**
 * Connect
 */
function mapStateToProps(state: IRPStore): IStateProps {
    return {
        offer: state.offerDetail.offer,
        articles: state.offerDetail.articles,
        promotions: state.offerDetail.promotions,
        variables: state.variables && state.variables.data,
        recommendedRegions: state.offerDetail.recommendedRegions,
        relatedOffers: state.offerDetail.relatedOffers
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        ...bindActionCreators({backToUrl}, dispatch)
    };
}

// Sub components
interface INearestOffersInfoProps {
    friendlyOfferListLink: string;
    regionStatsForSale?: number | false;
    currentStageOfferCount?: number | null;
}

function NearestOffersInfo(props: INearestOffersInfoProps) {
    const {regionStatsForSale, friendlyOfferListLink, currentStageOfferCount} = props;
    const saleStats = currentStageOfferCount ? currentStageOfferCount : regionStatsForSale;
    if (!saleStats) {
        return;
    }
    return (
        <Text variant="body_copy_2" css={pt(2)}>
            {`Mamy dla Ciebie `}
            <a css={noWrapUnderlinedStyle} href={friendlyOfferListLink}>
                {saleStats} {offerPlural(saleStats)}
            </a>
            {currentStageOfferCount ? ` w kolejnym etapie tej inwestycji!` : " w najbliższej okolicy!"}
        </Text>
    );
}

function InactiveInfo() {
    return (
        <Text variant="body_copy_2">
            Uwaga! Wizytówka niniejszej inwestycji jest nieaktywna. Informacje na jej temat pomagają nam jednak tworzyć wiarygodną bazę informacji na temat
            lokalnego rynku nieruchomości.
        </Text>
    );
}

//  Styles
const headerWrapper = (theme: Theme) => css`
    ${mv(2)};

    @media (min-width: ${theme.breakpoints.lg}) {
        ${mt(3)};
        ${mb(2)};
    }
`;

const linksWrapperStyle = (theme: Theme) => css`
    ${p(0)};
    background-color: #fff;

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(8, 0)};
    }
`;

const mainMessageWrap = css`
    ${mb(2)}
    ${elevation(0)}
    ${borderRadius(2)}
    ${p(2)}
    background: #fff;
`;

const mainMessageTitleStyle = css`
    ${mb(2)}
`;

const noWrapUnderlinedStyle = css`
    text-wrap: unset;
    text-decoration: underline;
`;

const subscribeButtonStyle = css`
    width: 100%;
    margin: 0 0 ${calculateRemSize(3)} 0;
`;

const mapWrapper = (theme: Theme) => css`
    ${mb(3)};
    position: relative;
    width: 100%;
    height: 500px;

    @media (min-width: ${theme.breakpoints.sm}) {
        height: 600px;
    }
`;

const planner3DStyle = (theme: Theme) => css`
    display: inline-block;

    @media (min-width: ${theme.breakpoints.sm}) {
        display: none;
    }
`;
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {connect, useSelector} from "react-redux";
import {matchPath, useLocation} from "react-router";
import {css, Theme} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {CenterBox, Hidden, WideContent} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, mb, mt, mv, p, pl, pt} from "@pg-design/helpers-css";
import {BrandBuildingIcon, BrandMapPinIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useChangeVisibility, useIsMounted} from "@pg-mono/hooks";
import {rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {rpCommonUrl} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {PhoneModal} from "../../application/components/phone_modal/PhoneModal";
import {ApplicationSource} from "../../application/utils/ApplicationSource";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {Heading} from "../../atoms/Heading";
import {LocationLinks} from "../../atoms/LocationLinks";
import {useOfferBuildings} from "../../hooks/useOfferBuildings";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {FavouritesInfoModal} from "../../my_offers/components/FavouritesInfoModal/FavouritesInfoModal";
import {backToUrl, IBackToUrlAction} from "../../navigation/actions/back_to_url_actions";
import {SubscribeButton} from "../../real_estate/components/SubscribeButton";
import {Country} from "../../region/types/Country";
import {offerDetailPageview} from "../../tracking/algolytics_hits/offer_detail_pageview";
import {IGtmRegion} from "../../tracking/google_tag_manager/calculate_gtm_dimensions";
import {gtmOfferGalleryEvents} from "../../tracking/google_tag_manager/gtm_event_offer_gallery_modal";
import {gtmInvestDetailOnOfferDetail} from "../../tracking/google_tag_manager/gtm_invest_detail";
import {IVariables} from "../../variables/reducers/variables_reducer";
import {ViewType} from "../../view_type/ViewType";
import {OfferAndPropertyBreadcrumbs} from "../atoms/OfferAndPropertyBreadcrumbs";
import {IRecommendedRegion} from "../atoms/OfferLocationLinksRecommendedRegions";
import {OfferLocationLinksWrapper} from "../atoms/OfferLocationLinksWrapper";
import {DetailConstructionGalleryCTA} from "../components/DetailConstructionGalleryCTA";
import {GalleryHolder} from "../components/gallery_holder/GalleryHolder";
import {NearbyOffersMap} from "../components/NearbyOffersMap";
import {OfferDetailHeader} from "../components/offer_detail_header/OfferDetailHeader";
import {IOfferDetailPromotion} from "../detail/actions/fetch_offer_detail_promotions";
import {OfferDetailForSale} from "../detail/components/OfferDetailForSale";
import {offerDescriptionSectionHeader, OfferDetailLimited} from "../detail/components/OfferDetailLimited";
import {OfferDetailStages} from "../detail/components/stages/OfferDetailStages";
import {Planner3dModal} from "../detail/planner3D/components/Planner3D";
import {SchemaOfferDetail} from "../detail/schema/SchemaOfferDetail";
import {SchemaOfferDetailRating} from "../detail/schema/SchemaOfferDetailRating";
import {buildFriendlyOfferListLink} from "../helpers/friendly_offer_list/build_friendly_offer_list_link";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {IOfferArticle} from "../list/actions/fetch_offer_list_articles";
import {IRelatedOfferList} from "../list/types/IOfferListResponse";
import {IOfferDetail} from "../types/IOfferDetail";
import {OfferModalActiveTab} from "../types/OfferModalActiveTab";
import {OfferType} from "../types/OfferType";
import {getOfferDisplayStatus} from "../utils/get_offer_display_status";
import {isOfferArchived} from "../utils/is_offer_archived";
import {isOfferFavouriteButtonAvailable} from "../utils/is_offer_favourite_button_available";
import {offerPlural} from "../utils/offer_plural";

const OfferChatBot = React.lazy(() => import("../detail/components/OfferChatBot"));

interface IStateProps {
    offer: IOfferDetail | null;
    articles: IOfferArticle[];
    recommendedRegions: IRecommendedRegion[];
    relatedOffers: IRelatedOfferList[];
    promotions: IOfferDetailPromotion[];
    variables: IVariables | null;
}

interface IActionsProps {
    backToUrl: (url: string, text: string) => IBackToUrlAction;
}

interface IOwnProps {}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

const OfferDetailViewC = (props: IProps) => {
    const {offer, promotions, variables, relatedOffers, recommendedRegions} = props;
    const viewType = useSelector((store: IRPStore) => store.viewType.current);
    const isGTMInitialized = useSelector((store: IRPStore) => store.googleTagManager);
    const {isMobile} = useUserDevice();

    const isMounted = useIsMounted();

    const getFriendlyOfferListLink = () => {
        const isFlat = offer?.type === OfferType.FLAT;
        const stats = offer?.region?.stats;

        const isEmpty = isFlat ? (stats?.properties_flats_count_for_sale ?? 0) <= 0 : (stats?.properties_houses_count_for_sale ?? 0) <= 0;

        const regionSlug = isEmpty ? offer?.region?.parent?.slug : offer?.region?.slug;
        const country = offer?.region?.country;
        const type = isFlat ? OfferType.FLAT : OfferType.HOUSE;

        return buildFriendlyOfferListLink({type, region: regionSlug, country});
    };

    const regionName =
        offer !== null &&
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.name
                : offer.region.name
            : offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.name
              : offer.region.name);

    useEffect(() => {
        if (offer) {
            offerDetailPageview(offer, offer.vendor);
        }
        const prevUrl = document.referrer;
        if (prevUrl !== "") {
            const url = new URL(prevUrl);
            const baseUrl = `${url.protocol}//${url.host}`;
            if (baseUrl === rpCommonUrl && backToPaths.some((path: string) => matchPath(url.pathname, {path: path, exact: true, strict: true}) !== null)) {
                props.backToUrl(prevUrl, "OFERTY");
                return;
            }
        }
        props.backToUrl(getFriendlyOfferListLink(), `oferty ${regionName}`);
        // props.gtmProductDetailOnOfferDetail();
        // props.gtmProductClickOnOfferDetail();
    }, [offer]);

    /*
    This particular event has to be fired only once. Event checks if gtm is initialized and offer data is available.
    Because of SSR offer is already available and dependency in useEffect is set to GTM flag from store.
    This is crucial for the event to fire in correct order.
     */
    useEffect(() => {
        if (offer && isGTMInitialized) {
            gtmInvestDetailOnOfferDetail(offer);
        }
    }, [isGTMInitialized]);

    const [isModalAndTabActive, _setIsModalAndTabActive] = useState<OfferModalActiveTab | null>(null);
    const setIsModalAndTabActive = (tab: OfferModalActiveTab | null) => {
        if (tab && isModalAndTabActive === null) {
            // send this GTM event only when opening modal
            gtmOfferGalleryEvents.onOfferGalleryModalOpen(tab, viewType);
        }
        _setIsModalAndTabActive(tab);
    };

    const backToPaths = [rpAppPath.friendlyOfferList, rpAppPath.multiOfferList.base, rpAppPath.offer.list];
    const isForSale = offer?.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isPlanned = offer?.configuration.display_type === OfferDisplayType.PLANNED;
    const isSold = offer?.configuration.display_type === OfferDisplayType.SOLD;
    const isLimited = offer && getOfferDisplayStatus(offer) === "limited";
    const isArchived = offer && isOfferArchived(offer);
    const isClassicView = isForSale && !isLimited;
    const enableChatBot = isMounted && isForSale && !isLimited; // disable ChatBot if isForSaleOverLimit
    const regionStatsForSale =
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.stats && offer.region.parent.stats.properties_flats_count_for_sale
                : offer.region.stats?.properties_flats_count_for_sale
            : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.stats && offer.region.parent.stats.properties_houses_count_for_sale
              : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale) || false;

    const refGallery = useRef<HTMLTableSectionElement>(null);
    const refProperties = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refDescription = useRef<HTMLTableSectionElement>(null);
    const refLocation = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(
        () => ({
            refGallery,
            refProperties,
            refDescription,
            refPromotions,
            refLocation,
            refContact
        }),
        []
    );

    const hasProperties = offer != null ? (offer.stats?.properties_count_for_sale ?? 0) > 0 : false;
    const hasPromotions = promotions.length > 0;
    const hasDescription = offer != null ? !!(offer.description || offer.configuration.show_generated_description) : false;

    const {offerBuildings, fetchOfferBuildings} = useOfferBuildings();

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const currentStageOfferCount = offer?.groups?.stages.filter((stage) => stage.current);
    const location = useLocation();
    useEffect(() => {
        if (!!isModalAndTabActive) {
            setModalIsOpen(true);
        }
    }, [isModalAndTabActive]);

    useEffect(() => {
        if (!modalIsOpen) {
            setIsModalAndTabActive(null);
        }
    }, [modalIsOpen]);

    const changeModalTab = useCallback(
        (tab: OfferModalActiveTab): void => {
            setIsModalAndTabActive(tab);

            offer?.configuration.show_building_models_on_map && fetchOfferBuildings(offer?.id);
        },
        [offer, fetchOfferBuildings]
    );

    // Scroll and visibility solution
    const getElems = useCallback(() => {
        const res = [];
        if (hasProperties) {
            res.push("properties");
        }
        if (hasPromotions) {
            res.push("promotions");
        }
        if (hasDescription) {
            res.push("description");
        }
        res.push("location");
        res.push("contact");
        return res;
    }, [hasProperties, hasPromotions, hasDescription]);

    const [visibleElem, changeVisibility, viewportHeight] = useChangeVisibility(getElems, 100);

    /**
     * Render
     */

    if (offer == null) {
        return (
            <CenterBox height="500px">
                <Loader size="lg" />
            </CenterBox>
        );
    }
    const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
        offer.region.stats || {};

    const breadcrumbs = {
        region_type_voivodeship: region_type_voivodeship || null,
        region_type_city: region_type_city || null,
        region_type_county: region_type_county || null,
        region_type_district: region_type_district || null,
        region_type_housing_estate: region_type_housing_estate || null,
        region_type_town: region_type_town || null
    };
    const {vendor} = offer;

    const renderOfferDetail = () => {
        if (isClassicView) {
            return (
                <OfferDetailForSale
                    offer={offer}
                    promotions={promotions}
                    variables={variables}
                    refElems={refElems}
                    visibleElement={visibleElem}
                    viewportHeight={viewportHeight}
                    changeVisibility={changeVisibility}
                    changeModalTab={changeModalTab}
                    hasProperties={hasProperties}
                    hasDescription={hasDescription}
                    shouldMapBeRendered={!modalIsOpen}
                />
            );
        } else {
            return (
                <OfferDetailLimited
                    offer={offer}
                    refElems={refElems}
                    variables={variables}
                    viewportHeight={viewportHeight}
                    changeVisibility={changeVisibility}
                    visibleElement={visibleElem}
                    changeModalTab={changeModalTab}
                    hasDescription={hasDescription}
                    shouldMapBeRendered={!modalIsOpen}
                />
            );
        }
    };

    return (
        <>
            <article>
                <section>
                    <WideContent css={mt(2)}>
                        <OfferAndPropertyBreadcrumbs
                            country={offer.region.country.toString()}
                            type={offer.type}
                            breadcrumbs={breadcrumbs}
                            offer={{name: offer.name, slug: offer.slug, id: offer.id}}
                            groups={offer.groups}
                            vendor={{name: vendor.name, id: vendor.id, slug: vendor.slug}}
                        />
                    </WideContent>
                </section>

                <WideContent css={headerWrapper}>
                    <OfferDetailHeader offer={offer} onLocationClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP)} />
                </WideContent>

                {isPlanned && !isArchived && (
                    <WideContent>
                        <div css={mainMessageWrap}>
                            <Text css={mainMessageTitleStyle}>Inwestycja planowana</Text>
                            <Text variant="body_copy_2">
                                Uwaga! Deweloper nie rozpoczął jeszcze sprzedaży mieszkań w tej inwestycji. {regionStatsForSale && `Mamy dla Ciebie`}{" "}
                                {regionStatsForSale && (
                                    <a css={noWrapUnderlinedStyle} href={getFriendlyOfferListLink()}>
                                        {regionStatsForSale} {offerPlural(regionStatsForSale)}
                                    </a>
                                )}{" "}
                                {regionStatsForSale && `w\xa0najbliższej okolicy!`}
                            </Text>
                            {isSold && (
                                <>
                                    <InactiveInfo />
                                    <NearestOffersInfo regionStatsForSale={regionStatsForSale} friendlyOfferListLink={getFriendlyOfferListLink()} />
                                </>
                            )}
                        </div>
                    </WideContent>
                )}

                {isArchived && !isPlanned && (
                    <>
                        <WideContent>
                            <div css={mainMessageWrap}>
                                <Text css={mainMessageTitleStyle}>Oferta archiwalna</Text>

                                {(isSold || isLimited) && (
                                    <>
                                        <InactiveInfo />
                                        <NearestOffersInfo
                                            regionStatsForSale={regionStatsForSale}
                                            friendlyOfferListLink={currentStageOfferCount ? location.pathname : getFriendlyOfferListLink()}
                                            currentStageOfferCount={
                                                currentStageOfferCount ? currentStageOfferCount[0].offer.stats.properties_count_for_sale : null
                                            }
                                        />
                                    </>
                                )}
                            </div>
                        </WideContent>
                        <OfferDetailStages offer={offer} isArchived />
                    </>
                )}

                {!isClassicView && (
                    <WideContent>
                        {relatedOffers.length > 0 && (
                            <>
                                <Heading type="h2" css={offerDescriptionSectionHeader}>
                                    Najbliższe inwestycje
                                </Heading>

                                <div css={mapWrapper}>
                                    <NearbyOffersMap
                                        viewType={ViewType.OFFER}
                                        originCountry={offer.region.country}
                                        originCoordinates={offer.geo_point.coordinates}
                                        offers={relatedOffers}
                                    />
                                </div>
                            </>
                        )}

                        {(isSold || (isLimited && !isPlanned)) && (
                            <Heading type="h2" css={offerDescriptionSectionHeader}>
                                Prezentacja oferty archiwalnej
                            </Heading>
                        )}
                    </WideContent>
                )}

                <section id="galeria" ref={refElems.refGallery}>
                    <GalleryHolder
                        source={ApplicationSource.Offer}
                        offer={offer}
                        offerBuildings={offerBuildings}
                        isOpen={modalIsOpen}
                        handleGalleryModalChange={(tab) => setIsModalAndTabActive(tab)}
                        modalState={isModalAndTabActive}
                        onModalClose={() => {
                            setModalIsOpen(false);
                        }}
                    />
                </section>
                <Hidden on="desktop">
                    <WideContent>
                        {isOfferFavouriteButtonAvailable(offer) && <SubscribeButton css={subscribeButtonStyle} offerId={offer.id} size="m" />}
                        <Planner3dModal offer={offer} vendorId={offer.vendor.id} css={planner3DStyle} routedModalId="view">
                            <Button iconLeft={BrandBuildingIcon} variant="brand_icon" size={isMobile ? "medium" : "big"} css={[mb(2), pl(0)]}>
                                Zobacz budynek 3D
                            </Button>
                        </Planner3dModal>
                        <Button
                            size="medium"
                            variant="brand_icon"
                            iconLeft={BrandMapPinIcon}
                            onClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP)}
                            css={[mb(2), pl(0)]}
                        >
                            Zobacz na mapie
                        </Button>
                        <DetailConstructionGalleryCTA onButtonClick={() => setIsModalAndTabActive(OfferModalActiveTab.CONSTRUCTION_GALLERY)} />
                    </WideContent>
                </Hidden>

                {renderOfferDetail()}

                {props.articles.length > 0 && <RelatedArticlesSection heading="Artykuły powiązane:" articles={props.articles} />}

                <SchemaOfferDetail offer={offer} />
                {!isSold && offer.is_active && <SchemaOfferDetailRating offer={offer} />}

                {/* For now, we don't want to show abroad regions on offers listing view - so we don't want to show links to listings */}
                {props.offer && props.offer.region.country === Country.POLAND && (
                    <OfferLocationLinksWrapper css={linksWrapperStyle}>
                        <LocationLinks recommendedRegions={recommendedRegions} offerType={offer.type} region={offer.region} relatedOffers={relatedOffers} />
                    </OfferLocationLinksWrapper>
                )}

                {/*    <LeadGeneratorSuccessInfo />*/}
            </article>
            <FavouritesInfoModal />
            <LeadModal />
            {enableChatBot && (
                <React.Suspense fallback={<React.Fragment />}>
                    <OfferChatBot hasPromotions={!!promotions.length} offer={offer} />
                </React.Suspense>
            )}

            {!isArchived && (
                <PhoneModal
                    source={ApplicationSource.OfferPhoneCall}
                    vendorLogo={offer.vendor.logo?.v_log_80x60}
                    vendorId={offer.vendor.id}
                    offerId={props.offer?.id}
                    region={props.offer?.region as IGtmRegion}
                />
            )}
        </>
    );
};

export const OfferDetailView = connect<IStateProps, IActionsProps, IOwnProps, IRPStore>(mapStateToProps, mapActionsToProps)(OfferDetailViewC);

/**
 * Connect
 */
function mapStateToProps(state: IRPStore): IStateProps {
    return {
        offer: state.offerDetail.offer,
        articles: state.offerDetail.articles,
        promotions: state.offerDetail.promotions,
        variables: state.variables && state.variables.data,
        recommendedRegions: state.offerDetail.recommendedRegions,
        relatedOffers: state.offerDetail.relatedOffers
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        ...bindActionCreators({backToUrl}, dispatch)
    };
}

// Sub components
interface INearestOffersInfoProps {
    friendlyOfferListLink: string;
    regionStatsForSale?: number | false;
    currentStageOfferCount?: number | null;
}

function NearestOffersInfo(props: INearestOffersInfoProps) {
    const {regionStatsForSale, friendlyOfferListLink, currentStageOfferCount} = props;
    const saleStats = currentStageOfferCount ? currentStageOfferCount : regionStatsForSale;
    if (!saleStats) {
        return;
    }
    return (
        <Text variant="body_copy_2" css={pt(2)}>
            {`Mamy dla Ciebie `}
            <a css={noWrapUnderlinedStyle} href={friendlyOfferListLink}>
                {saleStats} {offerPlural(saleStats)}
            </a>
            {currentStageOfferCount ? ` w kolejnym etapie tej inwestycji!` : " w najbliższej okolicy!"}
        </Text>
    );
}

function InactiveInfo() {
    return (
        <Text variant="body_copy_2">
            Uwaga! Wizytówka niniejszej inwestycji jest nieaktywna. Informacje na jej temat pomagają nam jednak tworzyć wiarygodną bazę informacji na temat
            lokalnego rynku nieruchomości.
        </Text>
    );
}

//  Styles
const headerWrapper = (theme: Theme) => css`
    ${mv(2)};

    @media (min-width: ${theme.breakpoints.lg}) {
        ${mt(3)};
        ${mb(2)};
    }
`;

const linksWrapperStyle = (theme: Theme) => css`
    ${p(0)};
    background-color: #fff;

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(8, 0)};
    }
`;

const mainMessageWrap = css`
    ${mb(2)}
    ${elevation(0)}
    ${borderRadius(2)}
    ${p(2)}
    background: #fff;
`;

const mainMessageTitleStyle = css`
    ${mb(2)}
`;

const noWrapUnderlinedStyle = css`
    text-wrap: unset;
    text-decoration: underline;
`;

const subscribeButtonStyle = css`
    width: 100%;
    margin: 0 0 ${calculateRemSize(3)} 0;
`;

const mapWrapper = (theme: Theme) => css`
    ${mb(3)};
    position: relative;
    width: 100%;
    height: 500px;

    @media (min-width: ${theme.breakpoints.sm}) {
        height: 600px;
    }
`;

const planner3DStyle = (theme: Theme) => css`
    display: inline-block;

    @media (min-width: ${theme.breakpoints.sm}) {
        display: none;
    }
`;
